import { AdminComment } from './AdminComment';
import { DealerContract } from './DealerContract';
import { Crop } from './Crop';
import { Dealer } from './Dealer';
import { Credit } from './Credit';
import { Order } from './Order';
import { Farm } from './Farm';
import { MoffinProfile } from './MoffinProfile';
import { State } from './State';
import { FinanceAccount } from './FinanceAccount';
import { SyntageProfile } from './SyntageProfile';

export interface User {
  address: string | null;
  addressExtended: string | null;
  associateIds: number[];
  avatar: string | null;
  birthday: string;
  ciec: string | null;
  ciecHidden: string | null;
  code: string;
  comments?: AdminComment[];
  companyName?: string;
  contactNextCycle?: string;
  contactOwner?: {name: string} | undefined;
  contracts?: DealerContract[];
  createdAt: string;
  credits?: Credit[];
  cropIds: number[];
  crops: Crop[];
  curp: string | null;
  dealerIds: number[];
  dealers: Dealer[];
  deniedCredit?: DeniedCreditProps;
  email: string;
  farms?: Farm[];
  financeAccount?: FinanceAccount | null;
  firstName: string;
  fullAddress: string | null;
  fullName: string;
  hasLogged?: boolean;
  history?: UserHistory[];
  hoursRegisterApplied: number | null;
  hubspotAt?: string;
  hubspotError?: Record<string, any>;
  hubspotId?: string;
  hubspotLink?: string;
  hubspotRequest?: Record<string, any>;
  hubspotResponse?: Record<string, any>;
  id: number;
  invitedByManagerDealerId?: number | null;
  invitedByManagerId?: number | null;
  isActive: boolean;
  isCompany: boolean;
  isEmailActivated: boolean;
  isInactive: boolean;
  isRenewal: boolean;
  isUnqualified: boolean;
  lastName: string;
  location?: Address[];
  mainBuyer?: Dealer | null;
  marriage: string | null;
  middleName: string | null;
  moffinProfiles?: MoffinProfile[];
  orders?: Order[];
  phone: string | null;
  profileProgress?: ProfileProgress | null;
  registeredAt: string;
  registrationId?: RegistrationId;
  rejectReason?: string;
  responsibleAdmin?: {name: string} | undefined;
  responsibleAdminId: number;
  retailerIds: number[];
  rfc: string | null;
  rfcCompany: string | null;
  rfcActual: string | null;
  sfAccountLink: string | null;
  sfContactId: string | null;
  sfContactLink: string | null;
  source: string | null;
  sourceEn: string | null;
  state?: State | null;
  stateId: number;
  status: string;
  statusEn: string;
  userType?: 'producer' | 'supplier' | 'aggregator' | 'other' | null;
  syntageProfile?: SyntageProfile | null;
  updatedAt: string;
}

export interface DeniedCreditProps {
  id: number;
  code: string;
  rejectReason: string;
}

export interface RegistrationId {
  value: boolean;
  fileUrls: {url: string | null; name: string}[];
  count: number;
}

export const marriageOptions = ['married', 'divorced', 'widow', 'single'].map(opt => ({
  id: opt,
  name: opt,
}));
export const genderOptions = ['Hombre', 'Mujer', 'Prefiero no decir'].map(opt => ({
  id: opt,
  name: opt,
}));
export const userTypeOptions = [
  { id: 'producer', name: 'Productor' },
  { id: 'supplier', name: 'Distribuidor de Insumos' },
  { id: 'aggregator', name: 'Acopiador' },
  { id: 'other', name: 'Otro' },
];
export const loginTryOptions = ['Logged in', 'Not Logged in'].map(opt => ({
  id: opt.toLowerCase(),
  name: opt,
}));
export const moffinOptions = ['Yes', 'No', 'Completed'].map(opt => ({
  id: opt.toLowerCase(),
  name: opt,
}));

export interface Address {
  extended: string;
  placeId: string;
  latitude: string;
  longitude: string;
  country: string;
  postalCode: string;
  state: string;
  city: string;
  neighbourhood: string;
  street: string;
  house: string;
  apartment: string;
  id?: number;
  userId?: number;
  original?: string;
  components?: any;
  formatted?: string;
}

export interface UserHistory {
  id: number;
  title: string;
  statusBefore: string | null;
  statusAfter: string | null;
  adminName?: string | null;
  createdAt: string;
  adminLink: string | null;
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  REJECTED = 'rejected',
  NON_VALID = 'non_valid',
}

export enum ProfileProgress {
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
}

export enum UserSource {
  ADWORDS = 'adwords',
  PARTNERS = 'partners',
  HUBSPOT = 'hubspot',
  OFF_TAKER = 'off_taker',
  ORGANIC = 'organic',
  OTHER_OFFLINE_CHANNELS = 'other_offline_channels',
  RETAIL_STORE = 'retail_store',
  STAND = 'stand',
  COLD_PROSPECT = 'cold_prospect',
}

export const userSourceMap = {
  [UserSource.ADWORDS]: 'Adwords',
  [UserSource.PARTNERS]: 'Partners',
  [UserSource.HUBSPOT]: 'CRM',
  [UserSource.OFF_TAKER]: 'Off takers',
  [UserSource.ORGANIC]: 'Organic',
  [UserSource.OTHER_OFFLINE_CHANNELS]: 'Offline Events',
  [UserSource.RETAIL_STORE]: 'Retailers',
  [UserSource.STAND]: 'Verqor stand',
  [UserSource.COLD_PROSPECT]: 'Cold prospect',
};

interface Option {
  id: UserSource;
  name: string;
  disabled?: boolean;
}

export const userSourceOptions: Option[] = [
  { id: UserSource.ADWORDS, name: userSourceMap[UserSource.ADWORDS] },
  { id: UserSource.PARTNERS, name: userSourceMap[UserSource.PARTNERS] },
  { id: UserSource.HUBSPOT, name: userSourceMap[UserSource.HUBSPOT] },
  { id: UserSource.OFF_TAKER, name: userSourceMap[UserSource.OFF_TAKER] },
  { id: UserSource.ORGANIC, name: userSourceMap[UserSource.ORGANIC] },
  { id: UserSource.OTHER_OFFLINE_CHANNELS, name: userSourceMap[UserSource.OTHER_OFFLINE_CHANNELS] },
  { id: UserSource.RETAIL_STORE, name: userSourceMap[UserSource.RETAIL_STORE] },
  { id: UserSource.STAND, name: userSourceMap[UserSource.STAND] },
  { id: UserSource.COLD_PROSPECT, name: userSourceMap[UserSource.COLD_PROSPECT] },
];
